import { useEffect, useState } from "react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

const setCanonical = (path: string) => {
  const href = `${window.location.origin}${path}`;
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

const Auth = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = mode === 'login' ? 'Login – Admin Upload' : 'Create account – Admin Upload';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Sign in or create an account to upload ads, brands and categories.');
    setCanonical('/auth');
  }, [mode]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        navigate('/admin', { replace: true });
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate('/admin', { replace: true });
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const signIn = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) return toast({ title: 'Login failed', description: error.message, variant: 'destructive' as any });
    toast({ title: 'Welcome back' });
  };

  const signUp = async () => {
    setLoading(true);
    const redirectUrl = `${window.location.origin}/admin`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl }
    });
    setLoading(false);
    if (error) return toast({ title: 'Signup failed', description: error.message, variant: 'destructive' as any });
    toast({ title: 'Check your email', description: 'Confirm your email to finish signup.' });
  };

  return (
    <div className="min-h-screen">
      <Header timeframe="all" showToggle={false} />
      <main className="max-w-md mx-auto px-6 py-10">
        <Card className="p-6 soft-shadow">
          <h1 className="text-xl font-semibold mb-1">{mode === 'login' ? 'Log in' : 'Create account'}</h1>
          <p className="text-sm text-muted-foreground mb-6">Access the admin upload tools.</p>

          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            {mode === 'login' ? (
              <Button onClick={signIn} disabled={loading || !email || !password}>{loading ? 'Signing in…' : 'Sign in'}</Button>
            ) : (
              <Button onClick={signUp} disabled={loading || !email || !password}>{loading ? 'Creating…' : 'Create account'}</Button>
            )}

            <Button type="button" variant="ghost" onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}>
              {mode === 'login' ? 'Need an account? Sign up' : 'Have an account? Log in'}
            </Button>

            <Button type="button" variant="secondary" onClick={() => navigate('/')}>Back to site</Button>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default Auth;
